import React, { useEffect, useState } from 'react'
import useSWR from 'swr'
import SpektrixFetcher from '@lib/spektrix/fetcher'
import BasketUpdateEvent from '@lib/basket-update-event'
import spektrixAuth from '@lib/spektrix/auth'
import LoginModal from '../login-modal'
import PriorityBookingErrorModal from '../priority-booking/error-modal'
import { addTicketsToBasket } from '@lib/spektrix'

const InstanceBookingButton = ({ 
  instanceId, 
  eventSlug, 
  eventName,
  dateSlug,
  timeSlug,
  dateTimeSlug, // Keep for backward compatibility
  isFree,
  isOnSale,
  isLocked,
  available,
  limitedAvailability,
  parentAndBaby,
  priorityBookingStartsAt // New prop for priority booking
}) => {
  // Convert string booleans to actual booleans
  const isFreeBoolean = isFree === 'true'
  const isOnSaleBoolean = isOnSale === 'true'
  const isLockedBoolean = isLocked === 'true'
  const availableNumber = available !== null && available !== '' ? parseInt(available, 10) : null
  const limitedAvailabilityBoolean = limitedAvailability === 'true'
  const parentAndBabyBoolean = parentAndBaby === 'true'
  
  // Check priority booking status
  const [authState, setAuthState] = useState({
    authenticated: false,
    user: null,
    loading: true
  })
  
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  
  // Priority booking is active if:
  // 1. priorityBookingStartsAt is set
  // 2. Current time is past the priority booking start time
  // 3. Either the event is not on sale OR it's locked (meaning general sale hasn't started)
  const isPriorityBookingActive = priorityBookingStartsAt ? 
    new Date() >= new Date(priorityBookingStartsAt) && (!isOnSaleBoolean || isLockedBoolean) : false
  
  
  // Check auth status for priority booking
  useEffect(() => {
    if (!isPriorityBookingActive) {
      setAuthState({ authenticated: false, user: null, loading: false })
      return
    }
    
    spektrixAuth.checkAuthStatus().then(result => {
      setAuthState({
        authenticated: result.authenticated,
        user: result.user,
        loading: false
      })
    })

    const cleanup = spektrixAuth.onAuthChange((authData) => {
      setAuthState({
        authenticated: authData.authenticated,
        user: authData.user,
        loading: false
      })
    })

    return cleanup
  }, [isPriorityBookingActive])
  
  // Check if user has active subscriptions/memberships
  const hasActiveSubscription = (user) => {
    if (!user || !user.subscriptions) {
      return false
    }
    
    // Check if any subscription is currently active
    const now = new Date()
    return user.subscriptions.some(subscription => {
      // Check if subscription has an end date and if it's in the future
      if (subscription.endDate) {
        const endDate = new Date(subscription.endDate)
        return endDate > now
      }
      
      // If no end date, consider it active if it has a start date in the past
      if (subscription.startDate) {
        const startDate = new Date(subscription.startDate)
        return startDate <= now
      }
      
      // If subscription exists but no dates, consider it active
      return true
    })
  }

  // Override booking logic for priority booking - requires active subscription
  const canBookDueToPriorityBooking = isPriorityBookingActive && 
                                      authState.authenticated && 
                                      hasActiveSubscription(authState.user)
  const effectiveIsOnSale = isOnSaleBoolean || canBookDueToPriorityBooking
  const effectiveIsLocked = isLockedBoolean && !canBookDueToPriorityBooking
  
  // Only fetch basket if this is a bookable instance (including priority booking)
  const shouldFetchBasket = effectiveIsOnSale && !effectiveIsLocked && availableNumber !== 0 && !isFreeBoolean
  
  const { data: basketContents, mutate } = useSWR(
    shouldFetchBasket ? '/basket' : null,
    SpektrixFetcher,
    {
      revalidateOnFocus: true,
      dedupingInterval: 5000 // Dedupe requests within 5 seconds
    }
  )

  // Listen for basket update events
  useEffect(() => {
    if (!shouldFetchBasket) return

    const handleBasketUpdate = () => {
      mutate()
    }

    window.addEventListener(BasketUpdateEvent, handleBasketUpdate)
    return () => {
      window.removeEventListener(BasketUpdateEvent, handleBasketUpdate)
    }
  }, [mutate, shouldFetchBasket])

  // Check if this instance is in the basket
  const ticketsInBasket = basketContents?.tickets?.filter(
    t => t.instance.id === instanceId
  )
  const inBasketCount = ticketsInBasket?.length || 0

  // Booking URL
  const bookingUrl = dateSlug && timeSlug 
    ? `/events/${eventSlug}/${dateSlug}/${timeSlug}/add_to_basket`
    : `/events/${eventSlug}/${dateTimeSlug}/add_to_basket`

  // Handle login success
  const handleLoginSuccess = (user) => {
    setShowLoginModal(false)
    setAuthState({
      authenticated: true,
      user: user,
      loading: false
    })
  }

  // Handle booking during priority period
  const handlePriorityBooking = async (e) => {
    e.preventDefault()
    
    try {
      // Attempt to add tickets to basket
      const response = await fetch(bookingUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'text/html,application/xhtml+xml',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (response.ok) {
        // Success - redirect to the booking URL
        window.location.href = bookingUrl
      } else {
        // Error - likely membership required
        // Show the error modal
        setShowErrorModal(true)
      }
    } catch (error) {
      console.error('Priority booking error:', error)
      // Show the error modal for any errors
      setShowErrorModal(true)
    }
  }

  // Determine button content and style
  if (isFreeBoolean) {
    return (
      <span className="badge badge-success badge-sm bg-green-600 text-white border-green-600 px-3 rounded-md">
        Free
      </span>
    )
  }

  // Show non-bookable states (unless priority booking allows it)
  if ((!effectiveIsOnSale || effectiveIsLocked) && !canBookDueToPriorityBooking) {
    // Show loading state during auth check for priority booking
    if (isPriorityBookingActive && authState.loading) {
      return (
        <span className="btn btn-outline btn-xs border-gray-400 text-gray-700 dark:border-gray-600 dark:text-gray-300 rounded-md">
          <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
        </span>
      )
    }
    
    // Priority booking specific states
    if (isPriorityBookingActive) {
      if (!authState.authenticated) {
        return (
          <>
            <button 
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setShowLoginModal(true)
              }}
              className="btn btn-primary btn-xs bg-blue-600 hover:bg-blue-700 text-white border-blue-600 hover:border-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 dark:border-blue-500 dark:hover:border-blue-600 rounded-md flex items-center gap-1"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
              </svg>
              Priority Booking
            </button>
            
            <LoginModal
              isOpen={showLoginModal}
              onClose={() => setShowLoginModal(false)}
              onLoginSuccess={handleLoginSuccess}
            />
          </>
        )
      } else if (!hasActiveSubscription(authState.user)) {
        return (
          <a 
            href="/memberships"
            className="btn btn-outline btn-xs border-amber-600 text-amber-700 hover:bg-amber-50 dark:border-amber-500 dark:text-amber-400 dark:hover:bg-amber-900/20 rounded-md flex items-center gap-1"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
            </svg>
            Membership
          </a>
        )
      }
    }
    
    return (
      <a 
        href="tel:01628788997" 
        className="btn btn-outline btn-xs border-gray-400 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 rounded-md flex items-center gap-1"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
        </svg>
        Call
      </a>
    )
  }

  if (availableNumber === 0) {
    return (
      <span className="badge badge-error badge-sm bg-red-600 text-white border-red-600 px-3 rounded-md">
        Sold Out
      </span>
    )
  }

  // Show basket count if items are in basket
  if (inBasketCount > 0) {
    return (
      <a 
        href={bookingUrl}
        className="btn btn-primary btn-xs bg-green-600 hover:bg-green-700 text-white border-green-600 hover:border-green-700 dark:bg-green-500 dark:hover:bg-green-600 dark:border-green-500 dark:hover:border-green-600 rounded-md"
      >
        {inBasketCount} in basket
      </a>
    )
  }

  // Regular booking button with parent_and_baby support
  const buttonClass = parentAndBabyBoolean
    ? "btn btn-primary btn-xs bg-[#f8ae41] hover:bg-[#f8ae41]/90 text-white border-[#f8ae41] hover:border-[#f8ae41]/90 dark:bg-[#f8ae41] dark:hover:bg-[#f8ae41]/90 dark:border-[#f8ae41] dark:hover:border-[#f8ae41]/90 rounded-md"
    : limitedAvailabilityBoolean
    ? "btn btn-primary btn-xs bg-amber-600 hover:bg-amber-700 text-white border-amber-600 hover:border-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600 dark:border-amber-500 dark:hover:border-amber-600 rounded-md"
    : "btn btn-primary btn-xs bg-blue-600 hover:bg-blue-700 text-white border-blue-600 hover:border-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 dark:border-blue-500 dark:hover:border-blue-600 rounded-md"

  const buttonText = parentAndBabyBoolean
    ? (
        <>
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C10.89 2 10 2.89 10 4C10 5.11 10.89 6 12 6C13.11 6 14 5.11 14 4C14 2.89 13.11 2 12 2M12 8C9.79 8 8 9.79 8 12V14H10V22H14V14H16V12C16 9.79 14.21 8 12 8Z"/>
          </svg>
          Book
        </>
      )
    : limitedAvailabilityBoolean
    ? "Book (Limited)"
    : "Book"

  // For priority booking with authenticated users who have membership, handle errors
  if (isPriorityBookingActive && authState.authenticated && hasActiveSubscription(authState.user)) {
    return (
      <>
        <a 
          href={bookingUrl}
          onClick={handlePriorityBooking}
          className={`${buttonClass} flex items-center`}
        >
          {buttonText}
        </a>
        
        <PriorityBookingErrorModal
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          eventName={eventName || 'this event'}
        />
      </>
    )
  }

  // Regular booking (no special handling needed)
  return (
    <a 
      href={bookingUrl}
      className={`${buttonClass} flex items-center`}
    >
      {buttonText}
    </a>
  )
}

export default InstanceBookingButton